import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { TopBar } from "./TopBar";
import { LeftRail } from "./LeftRail";
import { DesktopTitleBar } from "./DesktopTitleBar";
import { ChatDock } from "@/features/chat/ChatDock";
import { CallDock } from "@/features/chat/CallDock";
import { AiChatPanel } from "@/features/ai/AiChatPanel";
import { EventReminders } from "@/features/calendar/EventReminders";
import { TaskReminders } from "@/features/tasks/TaskReminders";
import { Toaster } from "@/lib/toast";
import { usePreferencesSync } from "@/lib/preferences";
import { AppFooter } from "@athavita/ui-theme";

// Loaded on first Ctrl+K, so the palette costs nothing until someone uses it.
const CommandPalette = lazy(() => import("./CommandPalette").then((m) => ({ default: m.CommandPalette })));

export function AppShell() {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [aiOpen, setAiOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [paletteLoaded, setPaletteLoaded] = useState(false);

    // Saved UI preferences: this browser's mirror ⇄ the account copy on the Mail BFF.
    usePreferencesSync();

    const openPalette = useCallback(() => {
        setPaletteLoaded(true);
        setPaletteOpen(true);
    }, []);

    // Ctrl/⌘+K anywhere — except inside a rich-text editor, where it inserts a link.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== "k") return;
            const target = e.target instanceof HTMLElement ? e.target : null;
            if (target && (target.isContentEditable || target.closest(".e-richtexteditor"))) return;
            e.preventDefault();
            setPaletteLoaded(true);
            setPaletteOpen((open) => !open);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
            {/* WCAG 2.1 SC 2.4.1: lets keyboard users skip the top bar + left
                rail and jump straight to the main content. Hidden visually
                until focused so it doesn't disturb the layout. */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-1 focus:text-sm focus:text-primary-foreground"
            >
                Skip to main content
            </a>
            <DesktopTitleBar />
            <TopBar
                searchInputRef={searchInputRef}
                onToggleAi={() => setAiOpen((o) => !o)}
                onOpenPalette={openPalette}
            />
            <div className="flex min-h-0 min-w-0 flex-1">
                <LeftRail />
                <main id="main-content" tabIndex={-1} className="flex min-h-0 min-w-0 flex-1">
                    <Outlet context={{ searchInputRef }} />
                </main>
            </div>
            <ChatDock />
            <CallDock />
            {/* One corner for calendar + task reminders so their cards stack instead of overlapping. */}
            <div
                role="region"
                aria-label="Reminders"
                className="pointer-events-none fixed bottom-14 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
            >
                <EventReminders />
                <TaskReminders />
            </div>
            <Toaster />
            {aiOpen && <AiChatPanel onClose={() => setAiOpen(false)} />}
            {paletteLoaded ? (
                <Suspense fallback={null}>
                    <CommandPalette
                        open={paletteOpen}
                        onOpenChange={setPaletteOpen}
                        searchInputRef={searchInputRef}
                        onToggleAi={() => setAiOpen((o) => !o)}
                    />
                </Suspense>
            ) : null}
            <AppFooter />
        </div>
    );
}
